import { useEffect, useState } from "react";
import TableHead from "@/components/tables/TableHead";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import { fetchAuditLogs } from "@/services/auditlogs.service";
import LogsTable from "@/components/tables/LogsTable";
import {
    getListCacheBootstrap,
    isListFullyFetched,
    writeListCache,
} from "@/utils/listPageCache";

const AuditLogs = () => {
    const [pageNo, setPageNo, dataLength, setDataLength, sorting, setSorting, _, __, searchItem, setSearchItem, resetDataQueries] =
        useDataQueries();
    const listBootstrap = getListCacheBootstrap("audit-logs", { dataLength, sorting, searchItem });

    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [logs, setLogs] = useState<any>(listBootstrap.items);
    const [logsLeft, setLogsLeft] = useState(true);
    const [allFetched, setAllFetched] = useState(false);
    const [dataLoad, setDataLoad] = useState(!listBootstrap.hasCache);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);

    useEffect(() => {
        const { cacheKey, cached } = getListCacheBootstrap("audit-logs", {
            dataLength,
            sorting,
            searchItem,
        });

        setPageNo(1);
        setAllFetched(false);
        setLogsLeft(true);
        if (cached) {
            setLogs(cached.items);
            setTotalCount(cached.totalCount);
            setDataLoad(false);
            setIsLoading(false);
            if (isListFullyFetched(cached.items.length, cached.totalCount, dataLength as number)) {
                setAllFetched(true);
                setLogsLeft(false);
            }
        } else {
            setIsLoading(true);
            setDataLoad(true);
        }

        fetchAuditLogs(
            1,
            dataLength,
            sorting,
            searchItem,
            (response: any) => {
                const nextLogs = response.data.logs ?? [];
                writeListCache(cacheKey, nextLogs, response.data.total_count);
                setIsLoading(false);
                setDataLoad(false);
                setTotalCount(response.data.total_count);
                if (nextLogs.length < dataLength) {
                    setLogsLeft(false);
                    setAllFetched(true);
                }
                setLogs(nextLogs);
            },
            (error: any) => {
                setDataLoad(false);
                console.log(error);
                setIsLoading(false);
            }
        );
    }, [dataLength, sorting, searchItem]);

    useEffect(() => {
        const handleScroll = () => {
            if (allFetched || isLoading || !logsLeft) {
                return;
            }

            if (
                window.innerHeight + document.documentElement.scrollTop <
                document.documentElement.scrollHeight - 1
            ) {
                return;
            }

            const nextPage = pageNo + 1;
            setPageNo(nextPage);
            setIsLoading(true);

            fetchAuditLogs(
                nextPage,
                dataLength,
                sorting,
                searchItem,
                (response: any) => {
                    setIsLoading(false);
                    setTotalCount(response.data.total_count);
                    const nextBatch = response.data.logs ?? [];
                    if (nextBatch.length < dataLength) {
                        setLogsLeft(false);
                        setAllFetched(true);
                    }
                    setLogs((prev: any[]) => [...prev, ...nextBatch]);
                },
                (error: any) => {
                    console.log(error);
                    setIsLoading(false);
                }
            );
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [
        allFetched,
        isLoading,
        logsLeft,
        pageNo,
        dataLength,
        sorting,
        searchItem,
        setPageNo,
    ]);

    const search = (searchText: string) => {
        setPageNo(1);
        setLogsLeft(true);
        setAllFetched(false);
        setSearchItem(searchText);
    };

    const reset = () => {
        setLogsLeft(true);
        setAllFetched(false);
        resetDataQueries();
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id="role-table"
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    setDataLength={setDataLength as (dataLength: number) => void}
                    title="Audit Logs"
                    currentEnvo={logs.length}
                    totalEnvo={totalCount}
                    search={search}
                    reset={reset}
                />
            </div>
            <div className="gs-users-table-wrap">
                <LogsTable
                    id="role-table"
                    setLogs={setLogs}
                    isLoading={isLoading}
                    dataLength={dataLength as number}
                    sorting={sorting}
                    setSorting={setSorting as ({}) => void}
                    allFetched={allFetched}
                    logs={logs}
                    setPageNo={setPageNo}
                    dataLoad={dataLoad}
                    searchItem={searchItem as string}
                />
            </div>
        </div>
    );
};

export default AuditLogs;
